import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import {
  UNITS,
  buildProductName,
  isDerivedProductName,
  normalizeBarcode,
  parsePackSize,
  unitLabel,
  type CatalogLookup,
  type PhotoIdentification,
  type Product,
  type ProductInput,
  type VisionSuggestion,
} from '@almox/shared';
import { useAppData } from '../AppData.js';
import { PhotoAiButton } from './PhotoAiButton.js';
import { BrandDatalist, CategoryDatalist } from './ui.js';

// A camera so e carregada quando alguem pede para escanear: a biblioteca de
// decodificacao pesa mais que o resto do app somado.
const BarcodeScanner = lazy(() => import('./BarcodeScanner.js'));

/**
 * Rascunho do produto compartilhado pelas telas que cadastram ou corrigem o
 * catalogo (entrada pelo scanner e tela "Produtos"). Tudo texto: o formulario
 * e quem converte para o payload da API, e campo vazio vira `null` la.
 */
export interface ProductDraft {
  /** Preenchido quando o produto ja existe no catalogo. */
  productId: string | null;
  barcode: string;
  /** Rotulo exibido. Derivado de tipo + marca + tamanho ate o usuario digitar. */
  name: string;
  brand: string;
  productType: string;
  size: string;
  category: string;
  unit: string;
  /** Em dias; vazio deixa a estimativa por conta da categoria. */
  defaultShelfLifeDays: string;
  /** Unidades por embalagem (caixa de 20 ovos = "20"); vazio quando nao se aplica. */
  packSize: string;
  notes: string;
  photoUrl: string | null;
  /** De onde vieram os dados — orienta o usuario e decide o aviso da IA. */
  origin: CatalogLookup['origin'];
  /** Confianca da IA (0 a 1) quando a origem e a foto. */
  confidence?: number;
  /** Usuario confirmou que o produto nao tem codigo de barras (regra 4). */
  noBarcode?: boolean;
}

export const EMPTY_PRODUCT_DRAFT: ProductDraft = {
  productId: null,
  barcode: '',
  name: '',
  brand: '',
  productType: '',
  size: '',
  category: '',
  unit: 'un',
  defaultShelfLifeDays: '',
  packSize: '',
  notes: '',
  photoUrl: null,
  origin: 'none',
};

export function productToDraft(product: Product): ProductDraft {
  return {
    productId: product.id,
    barcode: product.barcode ?? '',
    name: product.name,
    brand: product.brand ?? '',
    productType: product.productType ?? '',
    size: product.size ?? '',
    category: product.category ?? '',
    unit: product.unit,
    defaultShelfLifeDays:
      product.defaultShelfLifeDays === null ? '' : String(product.defaultShelfLifeDays),
    packSize: product.packSize === null ? '' : String(product.packSize),
    notes: product.notes ?? '',
    photoUrl: product.photoUrl,
    origin: 'local',
  };
}

/** Payload de criacao/edicao do produto a partir do rascunho. */
export function draftToProductInput(draft: ProductDraft): ProductInput {
  const dias = Number(draft.defaultShelfLifeDays);
  const porEmbalagem = Number(draft.packSize);
  return {
    name: draft.name.trim() || buildProductName(draft),
    brand: draft.brand.trim() || null,
    productType: draft.productType.trim() || null,
    size: draft.size.trim() || null,
    category: draft.category.trim() || null,
    unit: draft.unit as ProductInput['unit'],
    notes: draft.notes.trim() || null,
    defaultShelfLifeDays: Number.isFinite(dias) && dias > 0 ? Math.round(dias) : null,
    packSize: Number.isFinite(porEmbalagem) && porEmbalagem > 1 ? porEmbalagem : null,
    ...(draft.photoUrl ? { photoUrl: draft.photoUrl } : {}),
    ...(draft.barcode.trim() ? { barcode: normalizeBarcode(draft.barcode) } : {}),
  };
}

/**
 * So os campos que mudaram em relacao ao produto do catalogo — para o PATCH
 * nao reescrever o que o usuario nem tocou. `null` quando nada mudou.
 */
export function changedProductFields(
  draft: ProductDraft,
  product: Product,
): Partial<ProductInput> | null {
  const alvo = draftToProductInput(draft);
  const atual: Partial<ProductInput> = {
    name: product.name,
    brand: product.brand,
    productType: product.productType,
    size: product.size,
    category: product.category,
    unit: product.unit as ProductInput['unit'],
    notes: product.notes,
    defaultShelfLifeDays: product.defaultShelfLifeDays,
    packSize: product.packSize,
    barcode: product.barcode,
  };

  const patch: Record<string, unknown> = {};
  for (const chave of Object.keys(atual) as Array<keyof typeof atual>) {
    const novo = chave === 'barcode' ? (alvo.barcode ?? null) : alvo[chave];
    if ((novo ?? null) !== (atual[chave] ?? null)) patch[chave] = novo ?? null;
  }
  return Object.keys(patch).length > 0 ? (patch as Partial<ProductInput>) : null;
}

/**
 * Aplica a leitura da IA no rascunho. O que a camera ou o usuario ja sabia
 * sobre o codigo de barras nunca e sobrescrito: dado conferido vale mais que
 * leitura de imagem.
 */
export function applyVisionSuggestion(
  draft: ProductDraft,
  suggestion: VisionSuggestion,
  barcodeLido: string | null,
): ProductDraft {
  const daEmbalagem = parsePackSize(suggestion.size);
  const proximo: ProductDraft = {
    ...draft,
    // "12 un" na embalagem ja deixa o produto pronto para a baixa unitaria.
    packSize: draft.packSize || (daEmbalagem ? String(daEmbalagem) : ''),
    productType: suggestion.productType || draft.productType,
    brand: suggestion.brand ?? draft.brand,
    size: suggestion.size ?? draft.size,
    category: suggestion.category ?? draft.category,
    unit: suggestion.unit || draft.unit,
    barcode: draft.barcode || (barcodeLido ?? ''),
    origin: 'photo',
    ...(suggestion.confidence !== undefined ? { confidence: suggestion.confidence } : {}),
  };
  // Nome ainda automatico continua acompanhando os campos; nome digitado fica.
  if (!draft.name.trim() || isDerivedProductName(draft.name, draft)) {
    proximo.name = buildProductName(proximo);
  }
  return proximo;
}

/**
 * Altera um campo mantendo o nome exibido em dia. Enquanto o nome for o
 * derivado, ele acompanha marca/tipo/tamanho; assim que o usuario escreve um
 * nome proprio, para de mexer.
 */
export function updateDraft(draft: ProductDraft, patch: Partial<ProductDraft>): ProductDraft {
  const proximo = { ...draft, ...patch };
  const mexeuNasPartes =
    patch.brand !== undefined || patch.productType !== undefined || patch.size !== undefined;
  if (mexeuNasPartes && patch.name === undefined && isDerivedProductName(draft.name, draft)) {
    proximo.name = buildProductName(proximo);
  }
  return proximo;
}

/** True quando falta vincular o codigo de barras a um produto lido pela IA. */
export function needsBarcodeLink(draft: ProductDraft): boolean {
  return draft.origin === 'photo' && !draft.barcode.trim() && !draft.noBarcode;
}

interface Props {
  value: ProductDraft;
  onChange: (next: ProductDraft) => void;
  /** Prefixo dos ids dos campos — a mesma tela pode ter dois formularios. */
  idPrefix: string;
  /**
   * Quando a tela ja tem uma camera propria (o scanner), ela cuida da leitura.
   * Sem isso, o proprio campo abre uma camera enxuta ao clicar em "Escanear".
   */
  onScanBarcode?: () => void;
  scanning?: boolean;
  autoFocus?: boolean;
  /**
   * Liga a leitura da embalagem por IA. O botao entra no fim do formulario e
   * cobra o codigo de barras antes da foto — quem trata a sugestao e a tela.
   */
  onPhotoResult?: (result: PhotoIdentification, photo: string) => void;
}

/**
 * Campos do cadastro de produto: marca, tipo, tamanho, categoria, unidade,
 * codigo de barras, validade padrao e observacoes — cada coisa no seu campo,
 * em vez de tudo espremido no nome.
 */
export function ProductFields({
  value,
  onChange,
  idPrefix,
  onScanBarcode,
  scanning,
  autoFocus,
  onPhotoResult,
}: Props) {
  const { categories, brands } = useAppData();
  const barcodeRef = useRef<HTMLInputElement | null>(null);
  const [cameraPropria, setCameraPropria] = useState(false);
  const precisaCodigo = needsBarcodeLink(value);
  const lendoCodigo = scanning ?? cameraPropria;

  // Vinculo pedido pela IA: o campo do codigo recebe o foco sozinho, senao o
  // aviso passa despercebido no meio do formulario.
  useEffect(() => {
    if (precisaCodigo) barcodeRef.current?.focus();
  }, [precisaCodigo]);

  const set = (patch: Partial<ProductDraft>) => onChange(updateDraft(value, patch));

  return (
    <>
      {value.origin === 'photo' ? (
        <p className="hint-ia">
          📷 Dados lidos da foto pela IA — confira antes de salvar.
          {typeof value.confidence === 'number' && value.confidence < 0.5
            ? ' A leitura ficou incerta.'
            : null}
        </p>
      ) : null}

      {precisaCodigo ? (
        <div className="prompt-barcode">
          <strong>Falta o codigo de barras.</strong> A IA leu a embalagem, mas quem identifica o
          produto nas proximas leituras e o codigo. Escaneie ou digite abaixo para vincular os dois.
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => set({ noBarcode: true })}
          >
            Este produto nao tem codigo de barras
          </button>
        </div>
      ) : null}

      <div className="field">
        <label htmlFor={`${idPrefix}-barcode`}>Codigo de barras</label>
        <div className="row" style={{ gap: 8 }}>
          <input
            id={`${idPrefix}-barcode`}
            ref={barcodeRef}
            value={value.barcode}
            onChange={(event) => set({ barcode: event.target.value, noBarcode: false })}
            placeholder="7891000100103"
            inputMode="numeric"
          />
          <button
            type="button"
            className="btn"
            style={{ flex: '0 0 auto' }}
            onClick={() =>
              onScanBarcode ? onScanBarcode() : setCameraPropria((ligada) => !ligada)
            }
          >
            {lendoCodigo ? '⏹ Parar' : '📷 Escanear'}
          </button>
        </div>
        {cameraPropria && !onScanBarcode ? (
          <Suspense fallback={<p className="small muted">Abrindo a camera…</p>}>
            <BarcodeScanner
              onDetect={(codigo) => {
                setCameraPropria(false);
                set({ barcode: normalizeBarcode(codigo), noBarcode: false });
              }}
              onCancel={() => setCameraPropria(false)}
            />
          </Suspense>
        ) : null}
        {value.noBarcode ? (
          <span className="small muted">
            Sem codigo: o Almox gera um codigo interno para reencontrar o produto depois.
          </span>
        ) : null}
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor={`${idPrefix}-type`}>Tipo de produto</label>
          <input
            id={`${idPrefix}-type`}
            value={value.productType}
            onChange={(event) => set({ productType: event.target.value })}
            placeholder="Ex.: Leite integral"
            autoFocus={autoFocus}
          />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-brand`}>Marca</label>
          <input
            id={`${idPrefix}-brand`}
            value={value.brand}
            onChange={(event) => set({ brand: event.target.value })}
            placeholder="Ex.: Italac"
            list="marcas"
          />
          <BrandDatalist brands={brands} />
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor={`${idPrefix}-size`}>Tamanho</label>
          <input
            id={`${idPrefix}-size`}
            value={value.size}
            onChange={(event) => set({ size: event.target.value })}
            placeholder="Ex.: 1 L, 500 g, 12 un"
          />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-unit`}>Unidade</label>
          <select
            id={`${idPrefix}-unit`}
            value={value.unit}
            onChange={(event) => set({ unit: event.target.value })}
          >
            {UNITS.map((option) => (
              <option key={option} value={option}>
                {unitLabel(option)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor={`${idPrefix}-category`}>Categoria</label>
          <input
            id={`${idPrefix}-category`}
            value={value.category}
            onChange={(event) => set({ category: event.target.value })}
            placeholder="Ex.: Laticinios"
            list="categorias"
          />
          <CategoryDatalist categories={categories} />
          <span className="small muted">Define a estimativa de validade.</span>
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-pack`}>Unidades por embalagem</label>
          <input
            id={`${idPrefix}-pack`}
            type="number"
            min={2}
            max={1000}
            step="1"
            value={value.packSize}
            onChange={(event) => set({ packSize: event.target.value })}
            placeholder="Ex.: 20"
          />
          <span className="small muted">
            Caixa com 20 ovos → 20. A entrada vira 20 unidades e a baixa sai de uma em uma.
          </span>
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-shelf`}>Validade padrao (dias)</label>
          <input
            id={`${idPrefix}-shelf`}
            type="number"
            min={1}
            max={3650}
            value={value.defaultShelfLifeDays}
            onChange={(event) => set({ defaultShelfLifeDays: event.target.value })}
            placeholder="Da categoria"
          />
          <span className="small muted">Em branco, vale a validade padrao da categoria.</span>
        </div>
      </div>

      <div className="field">
        <label htmlFor={`${idPrefix}-name`}>Nome exibido</label>
        <input
          id={`${idPrefix}-name`}
          value={value.name}
          onChange={(event) => onChange({ ...value, name: event.target.value })}
          placeholder={buildProductName(value) || 'Ex.: Leite integral Italac 1 L'}
        />
        <span className="small muted">
          {isDerivedProductName(value.name, value)
            ? 'Montado com tipo, marca e tamanho — edite se quiser outro nome.'
            : 'Nome proprio: nao acompanha mais os campos acima.'}
        </span>
      </div>

      <div className="field">
        <label htmlFor={`${idPrefix}-notes`}>Observacoes</label>
        <textarea
          id={`${idPrefix}-notes`}
          rows={2}
          value={value.notes}
          onChange={(event) => set({ notes: event.target.value })}
          placeholder="Ex.: so a versao sem lactose"
        />
      </div>

      {onPhotoResult ? (
        <PhotoAiButton
          barcode={value.barcode}
          noBarcode={value.noBarcode ?? false}
          onBarcode={(codigo) =>
            set(codigo ? { barcode: codigo, noBarcode: false } : { noBarcode: true })
          }
          onResult={onPhotoResult}
        />
      ) : null}
    </>
  );
}
